/**
 * Timesheet API Service
 * Handles all /v2/api/timesheet endpoints
 */

import apiClient from '../client';
import type {
  DailySummary,
  WeeklySummary,
  MonthlySummary,
  TimesheetDashboard,
  QuickStats,
  ChartData,
  PeriodComparison,
  YearOverYearComparison,
  PaginatedResponse,
  DailySummariesParams,
  WeeklySummariesParams,
  MonthlySummariesParams,
  QuickStatsParams,
  TrendChartParams,
  ProjectsChartParams,
  UsersChartParams,
  ExportParams,
  ComparePeriodParams,
  YearOverYearParams,
} from '../types/timesheet.types';

const BASE_URL = '/timesheet';

// ============================================================================
// Daily Summaries
// ============================================================================

export const getDailySummaries = async (
  params?: DailySummariesParams
): Promise<PaginatedResponse<DailySummary>> => {
  const response = await apiClient.get(`${BASE_URL}/summaries/daily`, {
    params,
  });
  return response.data;
};

export const getDailySummary = async (
  projectId: string,
  date: string
): Promise<DailySummary> => {
  const response = await apiClient.get(
    `${BASE_URL}/summaries/daily/project/${projectId}/date/${date}`
  );
  return response.data;
};

// ============================================================================
// Weekly Summaries
// ============================================================================

export const getWeeklySummaries = async (
  params?: WeeklySummariesParams
): Promise<PaginatedResponse<WeeklySummary>> => {
  const response = await apiClient.get(`${BASE_URL}/summaries/weekly`, {
    params,
  });
  return response.data;
};

export const getWeeklySummary = async (
  projectId: string,
  year: number,
  week: number
): Promise<WeeklySummary> => {
  const response = await apiClient.get(
    `${BASE_URL}/summaries/weekly/project/${projectId}/year/${year}/week/${week}`
  );
  return response.data;
};

// ============================================================================
// Monthly Summaries
// ============================================================================

export const getMonthlySummaries = async (
  params?: MonthlySummariesParams
): Promise<PaginatedResponse<MonthlySummary>> => {
  const response = await apiClient.get(`${BASE_URL}/summaries/monthly`, {
    params,
  });
  return response.data;
};

export const getMonthlySummary = async (
  projectId: string,
  year: number,
  month: number
): Promise<MonthlySummary> => {
  const response = await apiClient.get(
    `${BASE_URL}/summaries/monthly/project/${projectId}/year/${year}/month/${month}`
  );
  return response.data;
};

// ============================================================================
// Dashboard
// ============================================================================

export const getDashboard = async (): Promise<TimesheetDashboard> => {
  const response = await apiClient.get(`${BASE_URL}/dashboard`);
  return response.data;
};

export const getQuickStats = async (
  params: QuickStatsParams
): Promise<QuickStats> => {
  const response = await apiClient.get(`${BASE_URL}/dashboard/quick-stats`, {
    params,
  });
  return response.data;
};

// ============================================================================
// Charts
// ============================================================================

export const getTrendChart = async (
  params: TrendChartParams
): Promise<ChartData> => {
  const response = await apiClient.get(`${BASE_URL}/charts/trend`, { params });
  return response.data;
};

export const getProjectsChart = async (
  params: ProjectsChartParams
): Promise<ChartData> => {
  const response = await apiClient.get(`${BASE_URL}/charts/projects`, {
    params,
  });
  return response.data;
};

export const getUsersChart = async (
  params: UsersChartParams
): Promise<ChartData> => {
  const response = await apiClient.get(`${BASE_URL}/charts/users`, { params });
  return response.data;
};

// ============================================================================
// Export
// ============================================================================

export const exportCSV = async (params: ExportParams): Promise<Blob> => {
  const response = await apiClient.get(`${BASE_URL}/export/csv`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const exportExcel = async (params: ExportParams): Promise<Blob> => {
  const response = await apiClient.get(`${BASE_URL}/export/excel`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

// ============================================================================
// Comparison
// ============================================================================

export const comparePeriods = async (
  params: ComparePeriodParams
): Promise<PeriodComparison> => {
  const response = await apiClient.get(`${BASE_URL}/compare/periods`, {
    params,
  });
  return response.data;
};

export const compareYearOverYear = async (
  params?: YearOverYearParams
): Promise<YearOverYearComparison> => {
  const response = await apiClient.get(`${BASE_URL}/compare/year-over-year`, {
    params,
  });
  return response.data;
};

// ============================================================================
// Export all as default object
// ============================================================================

const timesheetService = {
  // Daily
  getDailySummaries,
  getDailySummary,

  // Weekly
  getWeeklySummaries,
  getWeeklySummary,

  // Monthly
  getMonthlySummaries,
  getMonthlySummary,

  // Dashboard
  getDashboard,
  getQuickStats,

  // Charts
  getTrendChart,
  getProjectsChart,
  getUsersChart,

  // Export
  exportCSV,
  exportExcel,

  // Comparison
  comparePeriods,
  compareYearOverYear,
};

export default timesheetService;
